import React, { useMemo } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * A tiny 12-cell year strip showing which months a plant is dormant.
 * Only renders when `months` is a non-empty array of ints 1-12.
 * The current calendar month is subtly highlighted with a ring so the
 * owner can locate "now" on the strip at a glance.
 *
 * Uses locale-aware single-letter month labels via Intl.DateTimeFormat
 * (Jan → "J" in English, "E" in Spanish, "1" in Japanese, etc.).
 *
 * Kept intentionally compact (~110px wide) so it fits inside plant cards
 * without competing with the care rows above it.
 */
export const DormancyTimeline = ({ months, testid = 'dormancy-timeline' }) => {
  const { language, t } = useLanguage();

  const dormantSet = useMemo(() => {
    if (!Array.isArray(months)) return null;
    const s = new Set();
    for (const m of months) {
      const mi = Number.isInteger(m) ? m : parseInt(m, 10);
      if (!Number.isNaN(mi) && mi >= 1 && mi <= 12) s.add(mi);
    }
    return s.size > 0 ? s : null;
  }, [months]);

  const labels = useMemo(() => {
    // `month: 'narrow'` gives single-letter labels in most locales; falls
    // back to a short 3-letter label if the runtime doesn't support it.
    try {
      const fmt = new Intl.DateTimeFormat(language, { month: 'narrow' });
      return Array.from({ length: 12 }, (_, i) => fmt.format(new Date(2000, i, 1)));
    } catch (_e) {
      return ['J','F','M','A','M','J','J','A','S','O','N','D'];
    }
  }, [language]);

  const monthNames = useMemo(() => {
    try {
      const fmt = new Intl.DateTimeFormat(language, { month: 'long' });
      return Array.from({ length: 12 }, (_, i) => fmt.format(new Date(2000, i, 1)));
    } catch (_e) {
      return ['January','February','March','April','May','June','July','August','September','October','November','December'];
    }
  }, [language]);

  if (!dormantSet) return null;

  const currentMonth = new Date().getMonth() + 1;
  const dormantLabel = t('dormancy') || 'Dormancy';

  return (
    <div
      className="flex items-center gap-2 mt-2"
      data-testid={testid}
      role="img"
      aria-label={`${dormantLabel}: ${Array.from(dormantSet).sort((a, b) => a - b).map((m) => monthNames[m - 1]).join(', ')}`}
    >
      <div className="flex gap-[2px] flex-shrink-0">
        {Array.from({ length: 12 }, (_, i) => {
          const monthNum = i + 1;
          const isDormant = dormantSet.has(monthNum);
          const isNow = monthNum === currentMonth;
          return (
            <span
              key={monthNum}
              // A little more vertical breathing room for the label, plus a
              // 2px ring for the current month so it's findable on the strip.
              className="inline-flex items-center justify-center text-[8px] font-semibold leading-none rounded-[3px]"
              style={{
                width: 12,
                height: 14,
                backgroundColor: isDormant ? 'hsl(230, 40%, 45%)' : 'hsl(143, 28%, 92%)',
                color: isDormant ? 'white' : 'hsl(125, 12%, 45%)',
                outline: isNow ? '1.5px solid hsl(21, 70%, 57%)' : 'none',
                outlineOffset: 0,
              }}
              title={`${monthNames[i]}${isDormant ? ` — ${dormantLabel}` : ''}${isNow ? ` (${t('dormantNow') || 'now'})` : ''}`}
              aria-hidden="true"
            >
              {labels[i]}
            </span>
          );
        })}
      </div>
    </div>
  );
};
